import { TRepeatableErrorHandler, TRepeatableFunction, RequestCancelError } from '@cian/valuation-utils-component';
import { FormValidationError } from '@cian/frontend-request-form-widget';

import { TThunkAction } from '../../types/redux';
import { ELegalCheckUpdateErrorMessages } from './constants';
import { UpdateLegalCheckService } from '../../services/legalCheck';
import { setIsRegistered, setLegalCheckId, setLegalCheckPrice } from './actionts';
import {
  fetchProcessFormAnswers,
  IProcessFormAnswersResponse200 as IRes200,
  IProcessFormAnswersResponse400,
  TProcessFormAnswersResponse,
} from '../../repositories/legal-check/v1/process-form-answers';
import { EStepCode } from '../../types/requestFormSchema';
import { EObjectTypeOptions, IRequestFormValue } from '../../types/requestFormValue';
import { IRequestFormValidationError } from '../../types/requestFormValidation';
import { mapToFormValidationError } from '../../mappers/requestFormValidation';

export const DEFAULT_REPEAT_INTERVAL = 1000;
export const MAX_REPEAT_INTERVAL = 10000;
export const MAX_REPEAT_COUNT = 5;

export interface IUpdateLegalCheckProps {
  fromStep: EStepCode;
  formValue: IRequestFormValue;
}

export interface IProcessFormAnswersValidationFailed extends IProcessFormAnswersResponse400 {
  errors: IRequestFormValidationError[];
}

export function updateLegalCheck(props: IUpdateLegalCheckProps): TThunkAction<Promise<void>> {
  return async (dispatch, getState, context) => {
    const { logger, httpApi } = context;
    const {
      legalCheck: { id: legalCheckId },
    } = getState();

    const draftUpdateRequest: TRepeatableFunction<TProcessFormAnswersResponse> = cancellationToken => {
      return fetchProcessFormAnswers<IRes200, IProcessFormAnswersValidationFailed>({
        httpApi,
        config: { cancellationToken },
        parameters: {
          legalCheckId,
          stepCode: props.fromStep,
          ...props.formValue,
          /** Первый шаг всегда objectType, поэтому значение будет в любом случае **/
          objectType: props.formValue.objectType as EObjectTypeOptions,
        },
      });
    };

    const onDraftUpdateRequestError: TRepeatableErrorHandler = error => {
      logger.error(ELegalCheckUpdateErrorMessages.InvalidResponse, {
        message: error.message,
      });
    };

    try {
      const result = await UpdateLegalCheckService.start(draftUpdateRequest, onDraftUpdateRequestError, {
        maxRepeatCount: MAX_REPEAT_COUNT,
        maxRepeatInterval: MAX_REPEAT_INTERVAL,
        repeatInterval: DEFAULT_REPEAT_INTERVAL,
      });

      if (result.statusCode === 200) {
        dispatch(setIsRegistered(result.response.isRegistered));
        dispatch(setLegalCheckId(result.response.legalCheckId));

        if (result.response.serviceFee) {
          dispatch(setLegalCheckPrice(result.response.serviceFee));
        }

        return;
      }

      throw mapToFormValidationError({
        errors: result.response.errors,
        fallback: result.response.message,
      });
    } catch (err) {
      if (err instanceof RequestCancelError) {
        return;
      }

      if (err instanceof FormValidationError) {
        throw err;
      }

      logger.error(ELegalCheckUpdateErrorMessages.ErrorRequest, err);

      throw new Error(ELegalCheckUpdateErrorMessages.GeneralError);
    }
  };
}
